import React from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
// Mock data for campaign conversion funnel
const conversionData = [{
  name: 'Views',
  value: 5200,
  color: '#6E9FD9'
}, {
  name: 'Clicks',
  value: 3800,
  color: '#D9984B'
}, {
  name: 'Applied',
  value: 2100,
  color: '#9C7CB6'
}, {
  name: 'Completed',
  value: 1450,
  color: '#7FC29B'
}];
// Mock data for campaign code usage over time (last 30 days)
const generateCodeUsageData = () => {
  return Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    // Create a pattern with some variability
    let usageCount = 30 + Math.floor(Math.random() * 40);
    // Add a peak in the middle
    if (i > 10 && i < 20) {
      usageCount += 20 + Math.floor(Math.random() * 30);
    }
    return {
      date: date.toISOString().split('T')[0],
      count: usageCount
    };
  });
};
// Mock data for payment volume over time (last 30 days)
const generatePaymentVolumeData = () => {
  return Array.from({
    length: 30
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    // Create a pattern with some variability
    let amount = 1500 + Math.floor(Math.random() * 1000);
    // Add a peak in the middle
    if (i > 10 && i < 20) {
      amount += 1000 + Math.floor(Math.random() * 1500);
    }
    return {
      date: date.toISOString().split('T')[0],
      amount: amount
    };
  });
};
// Format date for display
const formatDate = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};
// Conversion Funnel Chart
export const ConversionFunnelChart = () => {
  const funnelSummary = conversionData.map(d => `${d.name} ${d.value.toLocaleString()}`).join(', ');
  return <div className="h-80" role="img" aria-label={`Conversion funnel: ${funnelSummary}`} data-id="element-10461">
      <ResponsiveContainer width="100%" height="100%" data-id="element-10462">
        <BarChart data={conversionData} layout="vertical" margin={{
        top: 20,
        right: 30,
        left: 40,
        bottom: 5
      }} data-id="element-10463">
          <CartesianGrid strokeDasharray="3 3" data-id="element-10464" />
          <XAxis type="number" data-id="element-10465" />
          <YAxis dataKey="name" type="category" data-id="element-10466" />
          <Tooltip formatter={value => [`${value.toLocaleString()}`, 'Count']} data-id="element-10467" />

          <Legend data-id="element-10468" />
          <Bar dataKey="value" name="Users" data-id="element-10469">
            {conversionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-10470" />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>;
};
// Conversion Rate Pie Chart
export const ConversionRatePieChart = () => {
  const {
    t
  } = useTranslation('shops');
  // Calculate conversion rates
  const viewToClick = (conversionData[1].value / conversionData[0].value * 100).toFixed(1);
  const clickToApply = (conversionData[2].value / conversionData[1].value * 100).toFixed(1);
  const applyToComplete = (conversionData[3].value / conversionData[2].value * 100).toFixed(1);
  const overallConversion = (conversionData[3].value / conversionData[0].value * 100).toFixed(1);
  const rateData = [{
    name: 'View → Click',
    value: parseFloat(viewToClick),
    color: '#6E9FD9'
  }, {
    name: 'Click → Apply',
    value: parseFloat(clickToApply),
    color: '#D9984B'
  }, {
    name: 'Apply → Complete',
    value: parseFloat(applyToComplete),
    color: '#9C7CB6'
  }];
  const rateSummary = rateData.map(d => `${d.name} ${d.value}%`).join(', ');
  return <div className="h-80" role="img" aria-label={`Conversion rates: ${rateSummary}. Overall conversion ${overallConversion}%.`} data-id="element-10471">
      <ResponsiveContainer width="100%" height="100%" data-id="element-10472">
        <PieChart data-id="element-10473">
          <Pie data={rateData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
          name,
          percent
        }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-10474">
            {rateData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-10475" />)}
          </Pie>
          <Tooltip formatter={value => [`${value}%`, 'Conversion Rate']} data-id="element-10476" />
          <Legend data-id="element-10477" />
        </PieChart>
      </ResponsiveContainer>
      <div className="text-center mt-2 text-sm font-medium text-gray-700" data-id="element-10478">
        {t('components.ConversionRatePieChart.overall_conversion')}{' '}
        {overallConversion}%
      </div>
    </div>;
};
// Code Usage Time Series Chart
export const CodeUsageTimeSeriesChart = () => {
  const codeUsageData = generateCodeUsageData();
  const total = codeUsageData.reduce((s, d) => s + d.count, 0);
  return <div className="h-80" role="img" aria-label={`Promo code usage over the last 30 days. Total uses: ${total.toLocaleString()}.`} data-id="element-10479">
      <ResponsiveContainer width="100%" height="100%" data-id="element-10480">
        <AreaChart data={codeUsageData} margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      }} data-id="element-10481">
          <CartesianGrid strokeDasharray="0" vertical={false} data-id="element-10482" />
          <XAxis dataKey="date" tickFormatter={formatDate} interval={4} data-id="element-10483" />
          <YAxis axisLine={false} data-id="element-10484" />
          <Tooltip formatter={value => [value.toLocaleString(), 'Usage Count']} labelFormatter={label => formatDate(label)} data-id="element-10485" />

          <Legend data-id="element-10486" />
          <Area type="monotone" dataKey="count" stroke="#5DAFBA" fill="#5DAFBA" fillOpacity={0.3} name="Code Usage" data-id="element-10487" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
};
// Payment Volume Time Series Chart
export const PaymentVolumeTimeSeriesChart = ({
  currency = 'MVR'
}) => {
  const paymentVolumeData = generatePaymentVolumeData();
  const total = paymentVolumeData.reduce((s, d) => s + d.amount, 0);
  return <div className="h-80" role="img" aria-label={`Payment volume over the last 30 days. Total: ${currency} ${total.toLocaleString()}.`} data-id="element-10488">
      <ResponsiveContainer width="100%" height="100%" data-id="element-10489">
        <AreaChart data={paymentVolumeData} margin={{
        top: 10,
        right: 30,
        left: 0,
        bottom: 0
      }} data-id="element-10490">
          <CartesianGrid strokeDasharray="0" vertical={false} data-id="element-10491" />
          <XAxis dataKey="date" tickFormatter={formatDate} interval={4} data-id="element-10492" />
          <YAxis axisLine={false} data-id="element-10493" />
          <Tooltip formatter={value => [`${currency} ${value.toLocaleString('en-US')}`, 'Volume']} labelFormatter={label => formatDate(label)} data-id="element-10494" />

          <Legend data-id="element-10495" />
          <Area type="monotone" dataKey="amount" stroke="#7FC29B" fill="#7FC29B" fillOpacity={0.3} name="Payment Volume" data-id="element-10496" />
        </AreaChart>
      </ResponsiveContainer>
    </div>;
};